import { useEffect, useLayoutEffect, useRef, type BaseSyntheticEvent } from 'react';
import { useIsFetching, useIsMutating, useMutation } from '@tanstack/react-query';
import { useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getFieldErrors, isApiError, isNotFoundError } from '@/services/api';
import {
    THIRD_PARTY_INVOICE_DEFAULTS,
    thirdPartyInvoiceSchema,
    toThirdPartyInvoiceRequest,
    type ThirdPartyInvoiceValues,
} from '../schemas/thirdPartyInvoice.schema';
import { submitThirdPartyInvoice } from '../services/thirdPartyAgreement';
import { thirdPartyAgreementKeys } from './thirdPartyAgreementKeys';
import { useThirdPartyInvoiceNotice, type ThirdPartyInvoiceNotice } from './useThirdPartyInvoiceNotice';
import type { ThirdPartyInvoiceRequest, ThirdPartyInvoicingContents } from '../types/thirdPartyAgreement.types';
import { thirdPartyInvoiceDisabledReason } from '../utils/thirdPartyInvoicingCopy';
import { isThirdPartyLineOpen, thirdPartyLineKey, type ThirdPartyInvoiceLine } from '../utils/thirdPartyInvoiceLine';

export type { ThirdPartyInvoiceNotice };

export interface ThirdPartyInvoiceSubmit {
    control: Control<ThirdPartyInvoiceValues>;
    submit: (event?: BaseSyntheticEvent) => Promise<void>;
    isSubmitting: boolean;
    disabledReason: string | null;
    notice: ThirdPartyInvoiceNotice | null;
}

interface Options {
    vrm: string;
    reference: string;
    visit: string;
    line: ThirdPartyInvoiceLine | null;
    dealerId: string | null;
    onSubmitted: () => void;
    refetchContents: () => Promise<{ data?: ThirdPartyInvoicingContents }>;
}

const REJECTED_CODE = 'third_party_invoice_rejected';
const FORM_FIELDS: Record<string, keyof ThirdPartyInvoiceValues> = {
    invoice_number: 'invoiceNumber',
    mileage: 'mileage',
};
const resolver = zodResolver(thirdPartyInvoiceSchema);

const isUnconfirmable = (error: unknown): boolean =>
    !isApiError(error) || error.status === undefined || error.status >= 500;

export function useThirdPartyInvoiceSubmit({
    vrm,
    reference,
    visit,
    line,
    dealerId,
    onSubmitted,
    refetchContents,
}: Options): ThirdPartyInvoiceSubmit {
    const agreement = `${vrm}:${reference}`;
    const lineKey = thirdPartyLineKey(line);
    const latest = useRef({ agreement, lineKey, visit });
    const mounted = useRef(false);
    const { notice, writeNotice } = useThirdPartyInvoiceNotice(vrm, reference, visit);
    const { control, handleSubmit, reset, setError, formState } = useForm<ThirdPartyInvoiceValues>({
        resolver,
        mode: 'onTouched',
        defaultValues: THIRD_PARTY_INVOICE_DEFAULTS,
    });
    const mutationKey = thirdPartyAgreementKeys.invoiceSubmit(vrm, reference);
    // Client-scoped, so the lock outlives a tab switch and holds until the contents refresh settles.
    const submissionPending = useIsMutating({ mutationKey }) > 0;
    const contentsRefreshing = useIsFetching({ queryKey: thirdPartyAgreementKeys.invoicing(vrm, reference) }) > 0;
    const locked = formState.isSubmitting || submissionPending || contentsRefreshing;
    const mutation = useMutation<void, unknown, ThirdPartyInvoiceRequest>({
        mutationKey,
        mutationFn: (invoice) => submitThirdPartyInvoice(vrm, reference, invoice),
        retry: false,
    });

    // Layout, not passive: a settling request must never see a committed switch with a stale ref.
    useLayoutEffect(() => {
        latest.current = { agreement, lineKey, visit };
    }, [agreement, lineKey, visit]);

    useLayoutEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        reset(THIRD_PARTY_INVOICE_DEFAULTS);
    }, [agreement, reset]);

    const agreementUnchanged = (): boolean => latest.current.agreement === agreement;
    const formStillOwnsLine = (): boolean =>
        mounted.current &&
        agreementUnchanged() &&
        latest.current.lineKey === lineKey &&
        latest.current.visit === visit;

    const succeed = (invoiceNumber: string): void => {
        if (formStillOwnsLine()) {
            reset(THIRD_PARTY_INVOICE_DEFAULTS);
            onSubmitted();
        }
        writeNotice({ kind: 'submitted', invoiceNumber });
    };

    const applyFieldErrors = (error: unknown): boolean => {
        const fieldErrors = getFieldErrors(error);
        let applied = false;
        for (const [field, formField] of Object.entries(FORM_FIELDS)) {
            const message = fieldErrors[field]?.[0];
            if (message) {
                setError(formField, { message }, { shouldFocus: !applied });
                applied = true;
            }
        }
        return applied;
    };

    const resolveUnconfirmed = async (submitted: ThirdPartyInvoiceLine, invoiceNumber: string): Promise<void> => {
        const refreshed = await refetchContents();
        if (!agreementUnchanged()) return;
        if (refreshed.data && !isThirdPartyLineOpen(refreshed.data, submitted)) {
            succeed(invoiceNumber);
        } else {
            writeNotice({ kind: 'unconfirmed' });
        }
    };

    const disabledReason = thirdPartyInvoiceDisabledReason(line, dealerId);

    const submit = handleSubmit(async (values) => {
        if (submissionPending || contentsRefreshing) return;
        if (disabledReason !== null || !line || line.contentId === null || !dealerId) return;
        writeNotice(null);

        try {
            await mutation.mutateAsync(toThirdPartyInvoiceRequest(values, line.contentId, dealerId));
        } catch (error) {
            if (!agreementUnchanged()) return;
            if (isUnconfirmable(error)) return resolveUnconfirmed(line, values.invoiceNumber);
            if (isNotFoundError(error)) return writeNotice({ kind: 'not-found' });
            const fieldsOnly = isApiError(error) && error.status === 422 && error.code !== REJECTED_CODE;
            if (fieldsOnly && formStillOwnsLine() && applyFieldErrors(error)) return;
            return writeNotice({ kind: 'rejected' });
        }

        if (!agreementUnchanged()) return;
        succeed(values.invoiceNumber);
        void refetchContents();
    });

    return { control, submit, isSubmitting: locked, disabledReason, notice };
}
